import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';

interface Subscription {
  id: string;
  user_id: string;
  tier: 'free' | 'starter' | 'professional' | 'enterprise' | null;
  status: 'active' | 'inactive' | 'cancelled' | null;
  created_at: string | null;
  updated_at: string | null;
  cancel_at_period_end?: boolean | null;
  current_period_end?: string | null;
  current_period_start?: string | null;
  stripe_customer_id?: string | null;
  stripe_subscription_id?: string | null;
}

interface SubscriptionContextType {
  subscription: Subscription | null;
  isLoading: boolean;
  error: Error | null;
  refetchSubscription: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

const isValidStatus = (status: string | null): status is 'active' | 'inactive' | 'cancelled' | null => {
  return status === null || ['active', 'inactive', 'cancelled'].includes(status);
};

const isValidTier = (tier: string | null): tier is 'free' | 'starter' | 'professional' | 'enterprise' | null => {
  return tier === null || ['free', 'starter', 'professional', 'enterprise'].includes(tier);
};

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchSubscription = async () => {
    if (!user) {
      setSubscription(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error) {
        throw error;
      }

      if (data) {
        if (!isValidStatus(data.status) || !isValidTier(data.tier)) {
          throw new Error('Invalid subscription data received');
        }
      }

      setSubscription(data as Subscription);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch subscription'));
      setSubscription(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscription();
  }, [user]);

  const refetchSubscription = async () => {
    await fetchSubscription();
  };

  return (
    <SubscriptionContext.Provider value={{ subscription, isLoading, error, refetchSubscription }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}; 